'use client';
import React, { ReactNode } from 'react';
import styled, { css } from '@benzinga/themetron';
import { InfoBarModule, InfoBarPriority } from './Modules';
import { safeDelay } from '@benzinga/safe-await';

export interface FadeConfig {
  duration: number;
  delay: number;
  gradientWidthPercentage: number;
}

export interface FadeConfig {
  duration: number;
  delay: number;
  gradientWidthPercentage: number;
}

export interface InfoBarInnerProps {
  fadeInConfig: FadeConfig;
  fadeOutConfig: FadeConfig;
  modules: InfoBarModule[];
  pauseAnimation?: boolean;
}

export interface InfoBarInnerState {
  topClass?: 'fadeIn' | 'fadeOut';
  bottomClass?: 'fadeIn' | 'fadeOut';
  currInfo: { node: ReactNode; width: number }[];
  nextInfo: { node: ReactNode; width: number }[];
}

export const InfoBarInner: React.FC<InfoBarInnerProps> = props => {
  const [state, setState] = React.useState<InfoBarInnerState>(() => ({
    currInfo: [],
    isHovered: false,
    nextInfo: [],
  }));
  const bottomBarRef = React.useRef<HTMLUListElement>(null);
  const topBarRef = React.useRef<HTMLUListElement>(null);
  const currInfoIdx = React.useRef(0);

  const pauseAnimation = React.useRef(props.pauseAnimation);
  pauseAnimation.current = props.pauseAnimation;

  const animate = React.useCallback((): (() => void) => {
    const fadeInConfig = props.fadeInConfig;
    const fadeOutConfig = props.fadeOutConfig;
    const exitRef = { exit: false };

    const switchInfo = () => {
      setState(state => ({
        ...state,
        bottomClass: undefined,
        currInfo: [...state.nextInfo],
        topClass: undefined,
      }));
    };

    const loadInfo = () => {
      const priorityModules = props.modules.filter(m => m.priority !== InfoBarPriority.Base);
      const baseModules = props.modules.filter(m => m.priority === InfoBarPriority.Base);

      //check priority modules before moving on to normal modules
      for (let i = 0; i < (priorityModules.length ?? 0); i++) {
        const module = priorityModules[i];
        const { items } = module.getInfoItems.next(bottomBarRef.current?.clientWidth ?? 0).value;

        if (items.length > 0) {
          setState(state => ({
            ...state,
            nextInfo: [...items],
          }));
          return;
        }
      }

      if (baseModules.length === 0) {
        setState(state => ({
          ...state,
          nextInfo: [{ node: <div>No data sources selected</div>, width: 0 }],
        }));
        return;
      }

      for (let i = 0; i < baseModules.length; i++) {
        const moduleIdx = (currInfoIdx.current + i + 1) % baseModules.length;
        const module = baseModules[moduleIdx];
        const { items } = module.getInfoItems.next(bottomBarRef.current?.clientWidth ?? 0).value;

        if (items.length > 0) {
          setState(state => ({
            ...state,
            nextInfo: [...items],
          }));
          currInfoIdx.current = moduleIdx;
          return;
        }

        //in case all modules return empty data, we just display the same data and try again next time.
      }
    };

    const startAnimation = () => {
      setState(state => ({
        ...state,
        bottomClass: 'fadeIn',
        topClass: 'fadeOut',
      }));
    };

    const waitPause = async (firstCall: boolean) => {
      if (pauseAnimation.current) {
        while (pauseAnimation.current) {
          await safeDelay(100);
        }
        await safeDelay(fadeOutConfig.delay * 1000).then(() => waitPause(false));
      } else if (firstCall) {
        await safeDelay(fadeOutConfig.delay * 1000).then(() => waitPause(false));
      }
    };

    setTimeout(async () => {
      while (exitRef.exit === false) {
        //load data
        loadInfo();

        //start animation
        startAnimation();

        //wait for animation to finish
        await safeDelay(Math.max(fadeInConfig.duration + fadeInConfig.delay, fadeOutConfig.duration) * 1000);

        //switch data between top and bottom bars
        switchInfo();

        //wait for fade out delay
        await waitPause(true);
      }
    });
    return () => {
      exitRef.exit = true;
    };
  }, [props.fadeInConfig, props.fadeOutConfig, props.modules]);

  React.useEffect(() => {
    const exitAnimation = { current: animate() };
    const subscriptions = props.modules.map(m =>
      m.subscribe(e => {
        switch (e.type) {
          case 'force_content_change': {
            exitAnimation.current();
            exitAnimation.current = animate();
            break;
          }
        }
      }),
    );
    return () => {
      exitAnimation.current();
      subscriptions.forEach(s => s.unsubscribe());
    };
  }, [animate, props.modules]);

  const mapItemsToElements = React.useCallback((items: { node: ReactNode; width: number }[]): React.ReactElement[] => {
    let totalWidth = 0;
    return items.reduce<React.ReactElement[]>((acc, info, j) => {
      if (totalWidth + info.width <= (bottomBarRef.current?.clientWidth ?? 0)) {
        totalWidth += info.width;
        if (acc.length > 0) {
          totalWidth += 4;
          acc.push(<InfoBarSpacer key={'spacer' + j} />);
        }
        acc.push(
          <InfoBarElement $targetWidth={info.width} key={j}>
            {info.node}
          </InfoBarElement>,
        );
      }
      return acc;
    }, []);
  }, []);

  return (
    <>
      <InfoBarElements
        $fadeInConfig={props.fadeInConfig}
        $fadeOutConfig={props.fadeOutConfig}
        className={`top ${state.topClass ?? ''}`}
        key={currInfoIdx.current} /* changing this key when data changes retriggers animation. This is necessary. */
        ref={topBarRef}
      >
        {state.currInfo.length ? (
          mapItemsToElements(state.currInfo)
        ) : (
          <InfoBarElement $targetWidth={80} key={0}></InfoBarElement>
        )}
      </InfoBarElements>
      <InfoBarElements
        $fadeInConfig={props.fadeInConfig}
        $fadeOutConfig={props.fadeOutConfig}
        className={`bottom ${state.bottomClass ?? ''}`}
        key={currInfoIdx.current + 1} /* changing this key when data changes retriggers animation. This is necessary. */
        ref={bottomBarRef}
      >
        {state.nextInfo.length ? (
          mapItemsToElements(state.nextInfo)
        ) : (
          <InfoBarElement $targetWidth={80} key={0}></InfoBarElement>
        )}
      </InfoBarElements>
    </>
  );
};

const InfoBarElements = styled.ul<{
  $fadeInConfig: FadeConfig;
  $fadeOutConfig: FadeConfig;
}>`
  @keyframes clip-fade {
    0% {
      mask-position: right;
    }
    100% {
      mask-position: left;
    }
  }

  position: absolute;
  padding: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-around;
  overflow: hidden;

  ${props => {
    const { gradientWidthPercentage: gWidth } = props.$fadeInConfig;

    return css`
      mask-size: ${gWidth + 200}% 100%;
    `;
  }}

  // by default top is visible.
  &.top {
    mask-image: linear-gradient(90deg, #fff, #fff);
  }

  // by default bottom is hidden.
  &.bottom {
    mask-image: linear-gradient(90deg, #0000, #0000);
  }

  // when fade in animation is triggered, top is visible.
  // mask is changed to a gradient that fades from white to black.
  // the size of the mask is (100% wide white area + 100% wide black area + width of the gradient from white to black) = (200 + gWidth)%
  // black section length is  100% of the bar. width relative to the mask is (100% / (200 + gWidth))%, starting at 0%.
  // white section length is  100% of the bar. width relative to the mask is (100% / (200 + gWidth))%, starting at ((100 + gWidth)% / (200 + gWidth))%.
  // fade out is the same, but the gradient is reversed.
  &.fadeIn {
    ${props => {
      const { delay, duration, gradientWidthPercentage: gWidth } = props.$fadeInConfig;
      return css`
        mask-image: linear-gradient(
          90deg,
          #fff ${(100 * 100) / (200 + gWidth)}%,
          #0000 ${((100 + gWidth) * 100) / (200 + gWidth)}%
        );
        animation: clip-fade ${duration}s linear;
        animation-fill-mode: both;
        animation-delay: ${delay}s; //delay for fade in is controlled by CSS, relative to when fade out animation starts.
      `;
    }}
  }

  &.fadeOut {
    ${props => {
      const { duration, gradientWidthPercentage: gWidth } = props.$fadeOutConfig;
      return css`
        mask-image: linear-gradient(
          90deg,
          #0000 ${(100 * 100) / (200 + gWidth)}%,
          #fff ${((100 + gWidth) * 100) / (200 + gWidth)}%
        );
        animation: clip-fade ${duration}s linear;
        animation-fill-mode: both;
        animation-delay: 0s; //delay for fade out is controlled by JS, animation starts immediately once JS triggers it.
      `;
    }}
  }
`;

const InfoBarElement = styled.li<{ $targetWidth?: number }>`
  display: inline-flex;
  padding: 0 0;
  flex-grow: 1;
  flex-basis: ${props => props.$targetWidth ?? 0}px;
  width: ${props => props.$targetWidth ?? 0}px;
  justify-content: space-around;
  align-self: center;
  list-style-type: none;
`;

const InfoBarSpacer = styled.li`
  display: inline-block;
  border-right: 4px solid ${props => props.theme.colors.border};
  width: 0px;
  flex-grow: 0;
`;
